// API base is served under the same origin: in `npm run dev`, Vite's dev-server
// proxy (frontend/vite.config.js) forwards /api to the backend on :8080; in
// prod, nginx (frontend/nginx.ecs.conf) proxies /api/ to Spring Boot on
// 127.0.0.1:8081. VITE_API_BASE can override for cross-origin setups.
import { getOktaAuth } from '../okta-config';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

// Reads the current access token from the shared OktaAuth singleton without
// triggering a renewal cycle (renewals are managed by the @okta/okta-react
// auto-renew loop set up in <Security>). Returns null when Okta isn't
// configured (anonymous mode) so we don't ship an Authorization header at
// all — sending an empty Bearer would make Spring Security reject the
// request with 401 even though the resource server is in permit-all mode.
function bearerHeader() {
  const oktaAuth = getOktaAuth();
  if (!oktaAuth) return null;
  const token = oktaAuth.getAccessToken();
  return token ? `Bearer ${token}` : null;
}

async function request(path, opts = {}) {
  const url = `${BASE}${path}`;
  const headers = { ...opts.headers };

  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(opts.body);
  }

  const auth = bearerHeader();
  if (auth && !headers.Authorization) {
    headers.Authorization = auth;
  }

  const res = await fetch(url, { ...opts, headers });
  const contentType = res.headers.get('content-type') || '';

  let data;
  if (contentType.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    const message =
      typeof data === 'object' ? data.message || res.statusText : res.statusText;
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export const api = {
  // Health
  getHealth: () => request('/health'),
  getLiveness: () => request('/health/live'),
  getReadiness: () => request('/health/ready'),

  // Items
  getItems: () => request('/items'),
  getItem: (id) => request(`/items/${id}`),
  createItem: (body) => request('/items', { method: 'POST', body }),
  updateItem: (id, body) => request(`/items/${id}`, { method: 'PUT', body }),
  deleteItem: (id) => request(`/items/${id}`, { method: 'DELETE' }),

  // Messaging
  publishSns: (body) => request('/publish/sns', { method: 'POST', body }),
  publishSqs: (body) => request('/publish/sqs', { method: 'POST', body }),

  // Upload
  uploadFile: (file) => {
    const form = new FormData();
    form.append('file', file);
    return request('/upload', { method: 'POST', body: form });
  },

  // Chaos
  getChaosStatus: () => request('/chaos/status'),
  toggleChaos: (enabled) =>
    request(`/chaos/toggle?enabled=${enabled}`, { method: 'POST' }),
  resetChaos: () => request('/chaos/reset', { method: 'POST' }),
  injectLatency: (delayMs) =>
    request(`/chaos/latency?delayMs=${delayMs}`, { method: 'POST' }),
  setPersistentLatency: (delayMs) =>
    request(`/chaos/latency/persistent?delayMs=${delayMs}`, { method: 'POST' }),
  randomLatency: (minMs = 100, maxMs = 1000) =>
    request(`/chaos/latency/random?minMs=${minMs}&maxMs=${maxMs}`),
  injectError: (statusCode, message) =>
    request(`/chaos/error/${statusCode}${message ? `?message=${encodeURIComponent(message)}` : ''}`),
  setErrorRate: (percentage) =>
    request(`/chaos/error/rate?percentage=${percentage}`, { method: 'POST' }),
  randomError: () => request('/chaos/error/random'),
  throwException: (type, message) =>
    request(`/chaos/exception?type=${encodeURIComponent(type)}&message=${encodeURIComponent(message)}`),
  allocateMemory: (megabytes) =>
    request(`/chaos/memory/allocate?megabytes=${megabytes}`, { method: 'POST' }),
  releaseMemory: () => request('/chaos/memory/release', { method: 'POST' }),
  cpuLoad: (durationMs, threads) =>
    request(`/chaos/cpu/load?durationMs=${durationMs}&threads=${threads}`, { method: 'POST' }),
  floodLogs: (count, level, message) =>
    request(
      `/chaos/logs/flood?count=${count}&level=${encodeURIComponent(level)}&message=${encodeURIComponent(message)}`,
      { method: 'POST' },
    ),
  structuredLogs: (count) =>
    request(`/chaos/logs/structured?count=${count}`, { method: 'POST' }),

  // Demo
  getDemo: () => request('/demo'),
  runDemo: () => request('/demo/run'),
};
